import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse, faEllipsis } from "@fortawesome/pro-solid-svg-icons";

import { IconButton, MenuList, MenuItem, useMenuList } from "kubra-ux-forge";

export interface BreadcrumbItem {
  label: string;
  to?: string;
  onClick?: () => void;
  isActive?: boolean;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  isMobile?: boolean;
  className?: string;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  isMobile = false,
  className = "breadcrumbs-container",
}) => {
  const { toggleMenu, isMenuOpen, closeMenu } = useMenuList({
    isMultiSelect: false,
    isOpen: false,
  });

  if (items.length === 0) {
    return null;
  }

  const shouldCondense = isMobile && items.length > 3;

  const renderBreadcrumbItem = (item: BreadcrumbItem) => {
    if (item.to) {
      return (
        <Link
          className="breadcrumb-item"
          to={item.to}
          onClick={item.onClick}
          data-cy={`breadcrumb-button-${item.label}`}
          data-label={item.label}
        >
          {item.label}
        </Link>
      );
    } else {
      return (
        <span
          className={
            item.isActive ? "breadcrumb-item-active" : "breadcrumb-item"
          }
          data-cy={`breadcrumb-text-${item.label}`}
        >
          {item.label}
        </span>
      );
    }
  };

  const renderHomeIcon = () => (
    <FontAwesomeIcon
      icon={faHouse}
      className="breadcrumb-home-icon"
      aria-label="Home"
    />
  );

  const renderMenuList = () => {
    if (!shouldCondense) return null;

    // Show items between first and last (excluding home and current page)
    const hiddenItems = items.slice(1, -1);

    return (
      <MenuList
        isOpen={isMenuOpen}
        controlComponent={
          <IconButton
            variant="simple"
            className="breadcrumb-ellipsis"
            icon={<FontAwesomeIcon icon={faEllipsis} />}
            onClick={toggleMenu}
          />
        }
        onOutsideClick={closeMenu}
      >
        {hiddenItems.map((item) => (
          <MenuItem
            key={`popover-${item.label}`}
            onClick={() => {
              if (item.onClick) {
                item.onClick();
              }

              closeMenu();
            }}
          >
            {item.to ? (
              <Link
                to={item.to}
                className="breadcrumb-popover-item-link"
                data-cy={`popover-button-${item.label}`}
              >
                {item.label}
              </Link>
            ) : (
              item.label
            )}
          </MenuItem>
        ))}
      </MenuList>
    );
  };

  if (shouldCondense) {
    const firstItem = items[0];
    const lastItem = items[items.length - 1];

    return (
      <div className={`${className} breadcrumb-mobile`}>
        <div className="breadcrumb-condensed">
          {/* Home icon for first item */}
          {firstItem.to ? (
            <Link
              className="breadcrumb-item breadcrumb-home"
              to={firstItem.to}
              onClick={firstItem.onClick}
              data-cy={`breadcrumb-home-${firstItem.label}`}
            >
              {renderHomeIcon()}
            </Link>
          ) : (
            <span className="breadcrumb-item breadcrumb-home">
              {renderHomeIcon()}
            </span>
          )}

          <span className="breadcrumb-separator"> / </span>

          {/* MenuList for hidden items */}
          {renderMenuList()}

          <span className="breadcrumb-separator"> / </span>

          {/* Last item */}
          {renderBreadcrumbItem(lastItem)}
        </div>
      </div>
    );
  }

  // Desktop/normal breadcrumb display.
  return (
    <div className={className}>
      {items.map((item, index) => (
        <React.Fragment key={`breadcrumb-${item.label}`}>
          {/* TODO: to be able to add this to Forge, we need to remove the Link component from
          react-router-dom and allow the breadcrumb item to be polymorphic using the as prop. */}

          {renderBreadcrumbItem(item)}
          {index < items.length - 1 && " / "}
        </React.Fragment>
      ))}
    </div>
  );
};
